import dayjs from 'dayjs';
import type { Duration } from 'dayjs/plugin/duration';
import type { Actor, ActorRef, ActorRefFrom, Snapshot } from 'xstate';
import { assign, enqueueActions, fromPromise, sendParent, sendTo, setup } from 'xstate';

export { createActor } from 'xstate';

import {
    humanMaxUserFileSize,
    isAllPagesOnOnePage,
    isBrowser,
    isNotRequiredAnswers,
    isPreview,
    MAX_USER_FILE_SIZE,
} from '@webapp/common/lib/const';
import { partsToSeconds } from '@webapp/common/lib/date';
import { fileSize } from '@webapp/common/lib/ui';
import { hasExtension } from '@webapp/common/lib/utils';
import { CANTRATE_ID } from '@webapp/common/resources/constants';
import type { MaxDiffBlockModel } from '@webapp/common/resources/entities';
import type {
    ConjointConfig,
    createQuestionRes,
    ForwardLogic,
    IQuestionCategory,
    PairComparisonParam,
    QuestionEntity,
} from '@webapp/common/resources/entities/question';
import { TestAnswerReactingState, withSelfAnswer } from '@webapp/common/resources/entities/question';
import type { NestedItem } from '@webapp/common/resources/question';
import { checkIsQWithClickReply, QuestionType } from '@webapp/common/resources/survey';

import { getApiClass } from '@webapp/survey/src/api/queries-api';
import type { QuestionAnswerActor, QuestionAnswerEntity } from '@webapp/survey/src/entities/answer-machine';
import { QuestionAnswerMachineDefinition } from '@webapp/survey/src/entities/answer-machine';
import type { QuestionGroupEntity } from '@webapp/survey/src/entities/question-group';
import type { QuestionResponseActor, ResponseEventParams } from '@webapp/survey/src/entities/response-machine';
import { QuestionResponseMachine } from '@webapp/survey/src/entities/response-machine';
import { surveyApi } from '@webapp/survey/src/machine/api';
import type { GroupMachineActor } from '@webapp/survey/src/machine/group-machine';
import { GroupMachineDefinition } from '@webapp/survey/src/machine/group-machine';
import {
    mapQuestionForQueue,
    replaceFileExt,
    replaceMaxCount,
    replaceMaxSize,
} from '@webapp/survey/src/machine/helpers/question';
import type { SavedQuestion } from '@webapp/survey/src/machine/lib';
import { loadTimer, shuffleAnswers, shuffleCategories } from '@webapp/survey/src/machine/lib';
import type { ResidenceActorRef } from '@webapp/survey/src/machine/residence';
import { ResidenceMachineDefinition } from '@webapp/survey/src/machine/residence';
import type { TimerMachineActor, TimerStopReason } from '@webapp/survey/src/machine/timer-machine';
import { TimerMachine } from '@webapp/survey/src/machine/timer-machine';

type MaxDiffBlocksChildEvents = { type: 'QUESTION_RESPONSE_CHANGE' } & ResponseEventParams;

type MaxDiffBlocksParentActor = ActorRef<Snapshot<unknown>, MaxDiffBlocksChildEvents>;

type MaxDiffBlocksEvent =
    | { type: 'GET_BLOCKS' }
    | { type: 'PREV_BLOCK' }
    | { type: 'NEXT_BLOCK' }
    | { type: 'ADD_ANSWER'; best: number | null; worst: number | null };

export enum MaxDiffBlocksState {
    INITIAL = 'inintial',
    LOADING = 'loading',
    SUCCESS_LOADED = 'success_loaded',
    FAILED_LOADED = 'failed_loaded',
}

const MaxDiffBlocksMachineDefinition = setup({
    types: {
        context: {} as {
            questionId: number;
            blocks: MaxDiffBlockModel;
            currentBlockIndex: number;
            answers: Record<number, { best: number | null; worst: number | null }>;
            parentRef: MaxDiffBlocksParentActor;
        },
        events: {} as MaxDiffBlocksEvent,
        input: {} as { questionId: number; parentRef: MaxDiffBlocksParentActor },
    },
    actors: {
        loadMaxDiff: fromPromise<{ blocks: MaxDiffBlockModel }, { questionId: number }>(
            async ({ input: { questionId } }) => {
                const blocks = await surveyApi.getMaxDiffBlocks(questionId);
                return { blocks };
            },
        ),
    },
}).createMachine({
    id: 'MaxDiffBlocksMachine',
    initial: MaxDiffBlocksState.INITIAL,
    context: ({ input: { questionId, parentRef } }) => ({
        blocks: [],
        questionId,
        currentBlockIndex: 0,
        answers: {},
        parentRef,
    }),

    states: {
        [MaxDiffBlocksState.INITIAL]: {
            on: {
                GET_BLOCKS: {
                    target: MaxDiffBlocksState.LOADING,
                },
            },
        },
        [MaxDiffBlocksState.LOADING]: {
            invoke: {
                id: 'loadMaxDiff',
                src: 'loadMaxDiff',
                input: ({ context: { questionId } }) => ({ questionId }),
                onDone: {
                    target: MaxDiffBlocksState.SUCCESS_LOADED,
                    actions: assign(({ event }) => {
                        return {
                            blocks: event.output.blocks,
                        };
                    }),
                },
            },
        },
        [MaxDiffBlocksState.SUCCESS_LOADED]: {
            on: {
                PREV_BLOCK: {
                    actions: assign({ currentBlockIndex: ({ context }) => Math.max(0, context.currentBlockIndex - 1) }),
                },
                NEXT_BLOCK: {
                    actions: assign({
                        currentBlockIndex: ({ context }) =>
                            Math.min(context.blocks.length - 1, context.currentBlockIndex + 1),
                    }),
                },
                ADD_ANSWER: {
                    actions: enqueueActions(({ event, context, enqueue }) => {
                        enqueue.sendTo(context.parentRef, {
                            type: 'QUESTION_RESPONSE_CHANGE',
                            questionId: context.questionId,
                            value: event.best,
                        });
                        enqueue.assign({
                            answers: {
                                ...context.answers,
                                [context.currentBlockIndex]: { worst: event.worst, best: event.best },
                            },
                            currentBlockIndex: Math.min(context.blocks.length - 1, context.currentBlockIndex + 1),
                        });
                    }),
                },
            },
        },
    },
});

type MaxDiffBlocksActorRef = ActorRefFrom<typeof MaxDiffBlocksMachineDefinition>;

const loadedNested = Object.create(null);

export const loadNestedItems = (
    { questionId, level, parent }: { questionId: number; level: number; parent: number | null },
    actor: NestedActorRef,
) => {
    const key = `${questionId}_${level}_${parent}`;

    if (loadedNested[key]) {
        actor.send({ type: 'ADD_NESTED', items: { [level]: loadedNested[key] } });
        return;
    }

    let nested: Array<NestedItem> = [];

    surveyApi.getNested(questionId, level, parent).then((res) => {
        ({ items: nested } = res);

        loadedNested[key] = nested;
        actor.send({ type: 'ADD_NESTED', items: { [level]: nested } });
    });
};

type NestedEvent =
    | { type: 'LOAD_NESTED'; level: number; parent: number | null }
    | { type: 'ADD_NESTED'; items: Record<string, Array<NestedItem>> }
    | { type: 'DELETE_NESTED'; item: string | number };

enum NestedState {
    INITIAL = 'inintial',
    LOADING = 'loading',
    SUCCESS_LOADED = 'success_loaded',
    FAILED_LOADED = 'failed_loaded',
}

const NestedMachineDefinition = setup({
    types: {
        context: {} as {
            nested: Record<number, Array<NestedItem>>;
            questionId: number;
        },
        events: {} as NestedEvent,
        input: {} as { questionId: number },
    },
}).createMachine({
    id: 'NestedMachine',
    initial: NestedState.INITIAL,
    context: ({ input: { questionId } }) => ({
        nested: [],
        questionId,
    }),
    states: {
        [NestedState.INITIAL]: {},
    },
    on: {
        ADD_NESTED: {
            reenter: true,
            actions: assign(({ event, context }) => {
                return {
                    nested: {
                        ...context.nested,
                        ...event.items,
                    },
                };
            }),
        },
        DELETE_NESTED: {
            reenter: true,
            actions: assign(({ event, context }) => {
                const newNested = { ...context.nested };
                delete newNested[event.item];

                return {
                    nested: newNested,
                };
            }),
        },
    },
});

type NestedActorRef = ActorRefFrom<typeof NestedMachineDefinition>;

/**
 const key = `${questionId}_${level}_${parentId}`;

        if (loadedNested[key]) return;

 let items = [];

 try {
 ({ items } = yield surveyApi.getNested(questionId, level, parentId));
 } catch (e) {
 return;
 }

 loadedNested[key] = true;

 const q = self.questions.find((q) => q.id === questionId);

 q.items.set(level, items);
 */
export type UploadedFile = {
    id: number;
    type: string | null;
    file: {
        id: number;
        name: string;
        path: string;
        size: number;
    };
};

export type QuestionEvent =
    | { type: 'SHOW'; readOnly?: boolean }
    | { type: 'HIDE' }
    | { type: 'TIMER_EXPIRED'; reason: TimerStopReason }
    | { type: 'VALIDATE' }
    | { type: 'TOUCH' }
    | { type: 'STOP_TIMER' }
    | { type: 'LOAD_RESIDENCE'; level: number; id: number }
    | { type: 'FILE_UPLOAD'; files: Array<File> }
    | { type: 'FILE_DELETE'; id: number }
    | ({ type: 'QUESTION_RESPONSE_CHANGE' } & ResponseEventParams)
    | { type: 'QUESTION_CHANGE_EXTRA'; value: string }
    | ({ type: 'QUESTION_RESPONSE_CHANGE_COMMENT' } & ResponseEventParams)
    | ({ type: 'QUESTION_RESPONSE_CHANGE_EXTRA' } & ResponseEventParams)
    | { type: 'QUESTION_TOUCH' }
    | { type: 'SET_TEST_RESULT'; currentPoints: number; maxPoints: number; isChecked: boolean }
    | { type: 'QUESTION_VALIDATE' }
    | { type: 'QUESTION_SAVE_INCOMPLETE' }
    | ({
          type: 'RESPONSE_CHANGE';
      } & ResponseEventParams)
    | ({
          type: 'GROUP_RESPONSE_CHANGE';
      } & ResponseEventParams)
    | ({
          type: 'RESPONSE_CHANGE_EXTRA';
      } & ResponseEventParams)
    | ({ type: 'RESPONSE_CHANGE_COMMENT' } & ResponseEventParams)
    | { type: 'TIMER_TICK' }
    | { type: 'SET_COMMENT_INVALID'; value: boolean }
    | { type: 'SET_INVALID'; value: boolean }
    | { type: 'SET_QUOTA_ERROR'; value: boolean }
    | { type: 'VALIDATE_FILES'; files: Array<File> }
    | { type: 'SET_SHOW_BY_LOGIC'; value: boolean }
    | { type: 'SET_ORDER'; value: number }
    | { type: 'SET_DETAILS_LOADED' }
    | { type: 'UPDATE_TRANSLATIONS'; question: QuestionEntity }
    | { type: 'SHOW_IMAGE' }
    | { type: 'CHECK_TEST_ANSWER'; data: Record<string, any> }
    | { type: 'HIDE_TEST_ANSWER_REACTING_NOTIFICATION' }
    | {
          type: 'CLICK_IMAGE';
          value?: {
              x: number;
              y: number;
          };
          cantRate?: boolean;
      };

export enum QuestionState {
    idle = 'idle',
    visible = 'visible',
    timer = 'timer',
    answered = 'answered',
    sent = 'sent',
    file_uploading = 'file_uploading',
    file_deleting = 'file_deleting',
    test_answer_checking = 'test_answer_checking',
}

export interface QuestionContext extends Omit<createQuestionRes, 'questionId'> {
    responseActor?: QuestionResponseActor;
    answersRaw?: Array<QuestionAnswerEntity>;
    // FIXME: тут должно быть rawAnswers
    answersActors?: Array<QuestionAnswerActor>;
    groups?: Array<QuestionGroupEntity>;
    groupsActors?: Array<GroupMachineActor>;
    timerLeft?: Duration;
    clickReply?: boolean;
    timer?: TimerMachineActor;
    residence?: Array<any>;
    residenceActor?: ResidenceActorRef;
    nestedActor?: NestedActorRef;
    maxDiffBlocksActor?: MaxDiffBlocksActorRef;
    files?: Array<UploadedFile>;
    parentRef?: ParentActor;
    showByLogic?: boolean;
    savedAnswer?: SavedQuestion;
    forwardLogic: Array<ForwardLogic>;
    readOnly?: boolean;
    pinned?: boolean;
    pairComparisonParams?: Array<PairComparisonParam>;
    clicks: Array<{ x: number; y: number; timeInMs: number; answerId: number }>;
    isImageHidden?: boolean;
    showImageTimestamp?: number;
    categories?: Array<IQuestionCategory>;
    conjointConfig?: ConjointConfig;
    testAnswerReactingState?: TestAnswerReactingState;
    // Вариант(ы) закрылись по квоте во время сабмита: показываем ошибку,
    // пока респондент не изменит ответ.
    quotaError?: boolean;
}

// https://dev.to/davidkpiano/release-notes-xstate-4-22-0-xstate-react-1-5-0-3d3k
// https://codesandbox.io/s/keen-grass-qiuz6?file=/src/index.tsx:0-9

type ChildEvents =
    | { type: 'QUESTION_TOUCHED'; questionId: number }
    | { type: 'QUESTION_SHOWN'; questionId: number }
    | { type: 'SELECT_NEXT_BUNDLE'; noValidate: boolean }
    | { type: 'SET_DETAILS_LOADED' };

type ParentActor = ActorRef<Snapshot<unknown>, ChildEvents>;

export const QuestionMachineDefinition = setup({
    types: {
        context: {} as QuestionContext,
        events: {} as QuestionEvent,
        input: {} as createQuestionRes & { parentRef: ParentActor; savedAnswer?: SavedQuestion },
    },
    actors: {
        uploadFile: fromPromise<Array<UploadedFile>, { context: QuestionContext; files: Array<File> }>(
            async ({ input: { files, context } }) => {
                const newFiles = await surveyApi.uploadFiles(context.id, files);

                return newFiles as Array<UploadedFile>;
            },
        ),
        deleteFile: fromPromise<{ id: number }, { context: QuestionContext; id: number }>(async ({ input: { id } }) => {
            await surveyApi.deleteFile(id);
            return { id };
        }),
        testAnswerCheck: fromPromise<{ correct: boolean }, { context: QuestionContext; data: Record<string, any> }>(
            async ({ input: { data } }) => {
                const { correct } = await surveyApi.setTestAnswer(data);

                return { correct };
            },
        ),
    },
}).createMachine({
    id: 'QuestionMachine',
    initial: QuestionState.idle,
    context: ({ input: { answers, parentRef, ...inputContext } }) => ({
        ...inputContext,
        dirty: Boolean(inputContext.savedAnswer),
        forwardLogic: inputContext.forwardLogic ?? [],
        parentRef,
        answersRaw: answers,
        clicks: [],
    }),
    entry: [
        assign({
            responseActor: ({ context, spawn, self }) => {
                return spawn(QuestionResponseMachine, {
                    systemId: `question-${context.id}-response`,
                    input: {
                        questionId: context.id,
                        parentRef: self,
                        savedResponse: context.savedAnswer?.response,
                    },
                });
            },
            answersActors: ({ context, spawn, self }) => {
                return context.answersRaw
                    .filter((a) => !a.excluded)
                    .map((a) =>
                        spawn(QuestionAnswerMachineDefinition, {
                            systemId: `question-${context.id}-answer-${a.id}`,
                            input: {
                                ...a,
                                questionId: context.id,
                                questionType: context.type,
                                id: a.id,
                                parentRef: self,
                                savedAnswer: context.savedAnswer?.answers?.find((savedA) => savedA.id === a.id),
                            },
                        }),
                    );
            },
            residenceActor: ({ context, spawn }) => {
                if (context.type !== QuestionType.RESIDENCE) {
                    return undefined;
                }
                return spawn(ResidenceMachineDefinition, {
                    input: {
                        questionId: context.id,
                    },
                });
            },
            nestedActor: ({ context, spawn }) => {
                if (context.type !== QuestionType.NESTED_LIST) {
                    return undefined;
                }

                return spawn(NestedMachineDefinition, {
                    input: {
                        questionId: context.id,
                    },
                });
            },
            maxDiffBlocksActor: ({ context, spawn, self }) => {
                if (context.type !== QuestionType.MAX_DIFF) {
                    return undefined;
                }

                return spawn(MaxDiffBlocksMachineDefinition, {
                    input: {
                        questionId: context.id,
                        parentRef: self,
                    },
                });
            },
            files: ({ context: { savedAnswer } }) => savedAnswer?.files || [],
            clicks: ({ context: { savedAnswer } }) => savedAnswer?.clicks || [],
            showImageTimestamp: ({ context: { savedAnswer } }) => savedAnswer?.showImageTimestamp || undefined,
            groupsActors: ({ context, spawn, self }) => {
                return context.groups.map((group) =>
                    spawn(GroupMachineDefinition, {
                        input: {
                            ...group,
                            questionId: context.id,
                            answerIds: context.answersRaw.map((a) => a.id),
                            parentRef: self,
                            savedResponse: context.savedAnswer?.groups?.find((savedG) => savedG.id === group.id),
                        },
                    }),
                );
            },
        }),
        assign(({ context }) => {
            if (!context.dirty) {
                return {};
            }
            const { params, responseActor, type } = context;
            let invalid = false;
            let commentInvalid = false;
            const api = getApiClass(type);
            if (type !== QuestionType.CSI && params.commentRequired && !responseActor.getSnapshot().context.comment) {
                commentInvalid = true;
            }
            if (!isNotRequiredAnswers && !api.validate(context)) {
                invalid = true;
            }

            return {
                valid: !invalid && !commentInvalid,
                invalid,
                commentInvalid,
            };
        }),
    ],
    states: {
        [QuestionState.idle]: {
            on: {
                SHOW: {
                    target: QuestionState.visible,
                },
            },
        },
        [QuestionState.visible]: {
            entry: enqueueActions(({ enqueue, context, event }) => {
                enqueue.sendTo(context.parentRef, { type: 'QUESTION_SHOWN', questionId: context.id });
                if (context.type === QuestionType.MAX_DIFF) {
                    context.maxDiffBlocksActor?.send({ type: 'GET_BLOCKS' });
                }
                if (context.params.randomOrder) {
                    enqueue.assign({
                        answersActors: ({ context }) => {
                            return shuffleAnswers(context.answersActors);
                        },
                    });
                }
                if (context.params.colsRandomOrder) {
                    enqueue.assign({
                        groupsActors: ({ context }) => {
                            return shuffleAnswers(context.groupsActors);
                        },
                    });
                }
                if (context.params.randomCategoriesOrder) {
                    enqueue.assign({
                        categories: ({ context }) => {
                            return shuffleCategories(context.categories);
                        },
                    });
                }
                if (context.params.timer && context.type !== QuestionType.FIRST_CLICK) {
                    if (!isBrowser) {
                        // TODO ?? purge
                        return;
                    }
                    // let hours, minutes, seconds;
                    // try {
                    //     ({ hours, minutes, seconds } = LocalStorage.get<SavedTimer>(storageTimerKey(ctx.id)));
                    // } catch (e) {
                    //     ({ hours, minutes, seconds } = ctx.params);
                    // }
                    // ({ hours, minutes, seconds } = ctx.params);
                    const isReadOnly = event.type === 'SHOW' && event.readOnly;
                    const isSavedTimer = loadTimer(`question-timer-${context.id}`);
                    if (isReadOnly ? isSavedTimer : !context.timer) {
                        enqueue.assign({
                            timer: ({ context, spawn, self }) => {
                                const timerActor = spawn(TimerMachine, {
                                    systemId: `question-timer-${context.id}`,
                                    input: {
                                        duration: partsToSeconds(
                                            context.params.hours,
                                            context.params.minutes,
                                            context.params.seconds,
                                        ),
                                        uniqueId: `question-timer-${context.id}`,
                                        parentRef: self,
                                    },
                                });
                                // Стартуем таймер сразу при показе вопроса: START из
                                // on.SHOW срабатывает только на повторный SHOW (возврат
                                // на вопрос), поэтому без этого на первом открытии
                                // вопроса таймер не тикал. FIGMA_TEST стартует сам
                                // после загрузки прототипа.
                                if (!isReadOnly && context.type !== QuestionType.FIGMA_TEST) {
                                    timerActor.send({ type: 'START' });
                                }
                                return timerActor;
                            },
                        });
                    }
                    enqueue.assign({
                        readOnly: isReadOnly,
                    });
                }
            }),
            on: {
                SHOW: {
                    actions: enqueueActions(({ context, event, enqueue }) => {
                        if (context.timer && context.type !== QuestionType.FIGMA_TEST) {
                            // В clickReply повторный SHOW приходит и при уходе с вопроса
                            // (бандл остаётся смонтированным, но становится readOnly).
                            // Неактивный вопрос ставим на паузу (STOP), активный —
                            // стартуем/возобновляем (START). Обе команды безопасны
                            // для таймера в любом состоянии.
                            // Отвеченный FIRST_CLICK (dirty) тоже держим на паузе:
                            // переответить его нельзя, таймеру нечего мерить.
                            const isAnsweredFirstClick = context.type === QuestionType.FIRST_CLICK && context.dirty;
                            context.timer.send({ type: event.readOnly || isAnsweredFirstClick ? 'STOP' : 'START' });
                        } else if (
                            context.readOnly &&
                            !event.readOnly &&
                            context.params.timer &&
                            !context.timer &&
                            context.type !== QuestionType.FIRST_CLICK
                        ) {
                            enqueue.assign({
                                timer: ({ context, spawn, self }) => {
                                    const timerActor = spawn(TimerMachine, {
                                        systemId: `question-timer-${context.id}`,
                                        input: {
                                            duration: partsToSeconds(
                                                context.params.hours,
                                                context.params.minutes,
                                                context.params.seconds,
                                            ),
                                            uniqueId: `question-timer-${context.id}`,
                                            parentRef: self,
                                        },
                                    });
                                    // Вопрос переходит из readOnly в редактируемый —
                                    // таймер должен стартовать сразу (кроме FIGMA_TEST,
                                    // он стартует сам после загрузки прототипа).
                                    if (context.type !== QuestionType.FIGMA_TEST) {
                                        timerActor.send({ type: 'START' });
                                    }
                                    return timerActor;
                                },
                            });
                        }
                        enqueue.assign({
                            readOnly: event.readOnly,
                        });
                    }),
                },
                SHOW_IMAGE: {
                    actions: enqueueActions(({ context, enqueue }) => {
                        enqueue.assign({
                            showImageTimestamp: Date.now(),
                        });
                        if (context.params.timer && !context.timer) {
                            enqueue.assign({
                                timer: ({ context, spawn, self }) => {
                                    const timerActor = spawn(TimerMachine, {
                                        systemId: `question-timer-${context.id}`,
                                        input: {
                                            duration: partsToSeconds(
                                                context.params.hours,
                                                context.params.minutes,
                                                context.params.seconds,
                                            ),
                                            uniqueId: `question-timer-${context.id}`,
                                            parentRef: self,
                                        },
                                    });
                                    // Таймер first-click стартует в момент, когда
                                    // респонденту открыли изображение.
                                    timerActor.send({ type: 'START' });
                                    return timerActor;
                                },
                            });
                        }
                    }),
                },
                CLICK_IMAGE: {
                    actions: enqueueActions(({ context, event, enqueue }) => {
                        const findDefaultMatchedArea = context.answersActors.find(
                            (actor) => actor.getSnapshot().context.answerArea.isDefault,
                        );

                        if (event.cantRate) {
                            enqueue.assign({
                                clicks: () => [],
                            });

                            if (findDefaultMatchedArea) {
                                const { response, id } = findDefaultMatchedArea.getSnapshot().context;

                                response.send({
                                    type: 'CHANGE',
                                    value: CANTRATE_ID,
                                    questionId: context.id,
                                    answerId: id,
                                });
                            }

                            return;
                        }
                        if (event.cantRate === false) {
                            // Снятие галочки «Не могу оценить»: зеркальный сброс —
                            // чистим клики и убираем CANTRATE_ID из response default-зоны.
                            enqueue.assign({
                                clicks: () => [],
                            });

                            if (findDefaultMatchedArea) {
                                const { response, id } = findDefaultMatchedArea.getSnapshot().context;

                                response.send({
                                    type: 'CHANGE',
                                    value: '',
                                    questionId: context.id,
                                    answerId: id,
                                });
                            }

                            return;
                        }
                        if (context.params.clickCount <= context.clicks.length) return;

                        let matchedArea = context.answersActors.find((actor) => {
                            const ctx = actor.getSnapshot().context;
                            if (!ctx.answerArea || ctx.answerArea.isDefault) return false;
                            return (
                                ctx.answerArea.x < event.value.x &&
                                ctx.answerArea.x + ctx.answerArea.width > event.value.x &&
                                ctx.answerArea.y < event.value.y &&
                                ctx.answerArea.y + ctx.answerArea.height > event.value.y
                            );
                        });
                        if (!matchedArea) {
                            matchedArea = findDefaultMatchedArea;
                        }
                        if (matchedArea) {
                            const areaAnswerId = matchedArea.getSnapshot().context.id;
                            enqueue.assign({
                                clicks: ({ context: { clicks } }) => [
                                    ...clicks,
                                    {
                                        x: event.value.x,
                                        y: event.value.y,
                                        timeInMs: Date.now() - context.showImageTimestamp,
                                        answerId: areaAnswerId,
                                    },
                                ],
                            });

                            matchedArea.getSnapshot().context.response.send({
                                type: 'CHANGE',
                                value: true,
                                questionId: context.id,
                                answerId: areaAnswerId,
                            });
                        }
                    }),
                },
                QUESTION_CHANGE_EXTRA: {
                    actions: ({ context, event: { value } }) => {
                        context.response.send({ type: 'CHANGE_EXTRA', value });
                    },
                },
                QUESTION_RESPONSE_CHANGE: {
                    actions: [
                        enqueueActions(({ context, event, enqueue }) => {
                            const api = getApiClass(context.type);

                            api.change(context, event);
                            enqueue.assign({
                                dirty: true,
                                // выбор изменился — квотная ошибка больше не актуальна
                                quotaError: false,
                            });
                            if (context.invalid) {
                                enqueue.raise({ type: 'VALIDATE' });
                            }
                        }),
                        sendTo(
                            ({ context }) => context.parentRef,
                            ({ context }) => {
                                return {
                                    type: 'QUESTION_TOUCHED',
                                    questionId: context.id,
                                };
                            },
                        ),
                        enqueueActions(({ enqueue }) => enqueue.raise({ type: 'TOUCH' })),
                        enqueueActions(({ enqueue, event, context }) => {
                            const replyByClick =
                                context.clickReply &&
                                !isAllPagesOnOnePage &&
                                checkIsQWithClickReply(context, event) &&
                                !withSelfAnswer(context.answersActors);

                            if (replyByClick) {
                                enqueue.raise({ type: 'VALIDATE' });

                                if (!context.invalid && !context.params.comment) {
                                    enqueue.sendTo(({ context }) => context.parentRef, {
                                        type: 'SELECT_NEXT_BUNDLE',
                                        noValidate: false,
                                    });
                                }
                            }
                        }),
                    ],
                },
                QUESTION_RESPONSE_CHANGE_EXTRA: {
                    actions: enqueueActions(({ enqueue, context }) => {
                        if (context.invalid) {
                            enqueue.raise({ type: 'VALIDATE' });
                        }
                        enqueue.sendTo(
                            ({ context }) => context.parentRef,
                            ({ context }) => {
                                return {
                                    type: 'QUESTION_TOUCHED',
                                    questionId: context.id,
                                };
                            },
                        );
                    }),
                },
                TIMER_EXPIRED: {
                    actions: enqueueActions(({ enqueue, context, event: { reason } }) => {
                        const now = dayjs();
                        const timer = context.timer.getSnapshot().context;
                        const allTime = now.add(timer.duration, 's');

                        const endTime = now.add(timer.duration * 1000 - timer.timeAgo, 'ms');

                        const factTime = dayjs.duration(allTime.diff(endTime));
                        const targetTime = dayjs.duration(allTime.diff(now));
                        enqueue.assign({
                            factTime,
                            targetTime,
                        });
                        if (
                            reason === 'expired' &&
                            !context.readOnly &&
                            !isPreview &&
                            context.type !== QuestionType.FIGMA_TEST
                        ) {
                            enqueue.sendTo(({ context }) => context.parentRef, {
                                type: 'SELECT_NEXT_BUNDLE',
                                noValidate: true,
                            });
                        }
                    }),
                },
                RESPONSE_CHANGE_COMMENT: {
                    actions: [
                        enqueueActions(({ context, enqueue }) => {
                            if (context.invalid || context.commentInvalid) {
                                enqueue.raise({ type: 'VALIDATE' });
                            }
                        }),
                        // Комментарий — часть ответа: без touch он не попадал
                        // в сохранение незавершёнки (ни в add/detail, ни в cache),
                        // если был последним действием на вопросе (#683).
                        // Пустое значение не трогаем: его шлют CSI/Kano при
                        // инициализации поля (commentRequired), это не ввод.
                        enqueueActions(({ enqueue, event }) => {
                            if (!event.value) {
                                return;
                            }
                            enqueue.raise({ type: 'TOUCH' });
                            enqueue.sendTo(
                                ({ context }) => context.parentRef,
                                ({ context }) => {
                                    return {
                                        type: 'QUESTION_TOUCHED',
                                        questionId: context.id,
                                    };
                                },
                            );
                        }),
                    ],
                },
                SET_COMMENT_INVALID: {
                    actions: assign(({ event: { value } }) => ({ commentInvalid: value })),
                },
                SET_INVALID: {
                    actions: assign(({ event: { value } }) => ({ commentInvalid: value })),
                },
                SET_QUOTA_ERROR: {
                    actions: assign(({ event: { value } }) => ({ quotaError: value })),
                },
                FILE_UPLOAD: {
                    target: QuestionState.file_uploading,
                },
                FILE_DELETE: {
                    target: QuestionState.file_deleting,
                },
                CHECK_TEST_ANSWER: {
                    target: QuestionState.test_answer_checking,
                },
            },
        },
        [QuestionState.file_uploading]: {
            invoke: {
                id: 'file_uploading',
                src: 'uploadFile',
                input: ({ context, event }) => ({ context, files: event.type === 'FILE_UPLOAD' ? event.files : [] }),
                onDone: {
                    target: QuestionState.visible,
                    actions: [
                        assign(({ context, event }) => {
                            return {
                                files: Array.isArray(context.files) ? context.files.concat(event.output) : event.output,
                                errorText: null,
                            };
                        }),
                        ({ context }) => {
                            context.responseActor.send({ type: 'CHANGE', value: '', questionId: context.id });
                        },
                    ],
                },
                onError: {
                    target: QuestionState.visible,
                    actions: assign(({ event }) => {
                        return {
                            errorText: (event.error as any).message,
                        };
                    }),
                },
            },
        },
        [QuestionState.file_deleting]: {
            invoke: {
                id: 'file_deleting',
                src: 'deleteFile',
                input: ({ context, event }) => ({ context, id: event.type === 'FILE_DELETE' ? event.id : 0 }),
                onDone: {
                    target: QuestionState.visible,
                    actions: [
                        assign(({ context, event }) => {
                            return {
                                files: Array.isArray(context.files)
                                    ? context.files.filter((file) => file.file.id !== event.output.id)
                                    : [],
                                errorText: null,
                            };
                        }),
                    ],
                },
                onError: {
                    target: QuestionState.visible,
                    actions: assign(({ event }) => {
                        return {
                            errorText: (event.error as any).message,
                        };
                    }),
                },
            },
        },
        [QuestionState.sent]: {},
        [QuestionState.test_answer_checking]: {
            invoke: {
                id: 'test_answer_check',
                src: 'testAnswerCheck',
                input: ({ context, event }) => ({
                    context,
                    data: (event as { data: any }).data,
                }),
                onDone: {
                    target: QuestionState.visible,
                    actions: [
                        assign(({ event }) => {
                            return {
                                testAnswerReactingState:
                                    event.output.correct === true
                                        ? TestAnswerReactingState.CORRECT
                                        : TestAnswerReactingState.INCORRECT,
                            };
                        }),
                    ],
                },
                onError: {
                    target: QuestionState.visible,
                    actions: assign(({ event }) => {
                        return {
                            errorText: (event.error as any).message,
                        };
                    }),
                },
            },
        },
    },
    on: {
        UPDATE_TRANSLATIONS: {
            actions: enqueueActions(({ event: { question }, context, enqueue }) => {
                context.answersActors?.forEach((actor) => {
                    const findAnswer = question.answers.find((answer) => answer.id === actor.getSnapshot().context.id);
                    if (findAnswer) {
                        actor.send({ type: 'UPDATE_TRANSLATIONS', answer: findAnswer });
                    }
                });
                context.groupsActors?.forEach((actor) => {
                    const findAnswer = question.groups.find((group) => group.id === actor.getSnapshot().context.id);
                    if (findAnswer) {
                        actor.send({ type: 'UPDATE_TRANSLATIONS', name: findAnswer.name });
                    }
                });
                const updatedParams =
                    context.type === QuestionType.FIGMA_TEST &&
                    !question.params.timer &&
                    Number((question.params as any).figmaTimerSeconds) > 0
                        ? {
                              ...question.params,
                              timer: true,
                              hours: 0,
                              minutes: 0,
                              seconds: Number((question.params as any).figmaTimerSeconds),
                          }
                        : question.params;

                enqueue.assign({
                    title: question.title,
                    params: updatedParams,
                    logics: question.logics,
                    errorText: question.errorText,
                });
            }),
        },
        SET_TEST_RESULT: {
            actions: assign(({ event: { currentPoints, maxPoints, isChecked } }) => ({
                currentPoints,
                maxPoints,
                isTestCheckedQuestion: isChecked,
            })),
        },
        SET_ORDER: {
            actions: assign(({ event }) => {
                return {
                    order: event.value,
                };
            }),
        },
        SET_SHOW_BY_LOGIC: {
            actions: assign(({ event }) => {
                return {
                    showByLogic: event.value,
                };
            }),
        },
        RESPONSE_CHANGE: {
            actions: enqueueActions(({ enqueue, context }) => {
                if (context.invalid) {
                    enqueue.raise({ type: 'VALIDATE' });
                }
                enqueue.sendTo(
                    ({ context }) => context.parentRef,
                    ({ context }) => {
                        return {
                            type: 'QUESTION_TOUCHED',
                            questionId: context.id,
                        };
                    },
                );
                enqueue.raise({ type: 'TOUCH' });
            }),
        },
        GROUP_RESPONSE_CHANGE: {
            actions: [
                ({ context, event }) => {
                    const api = getApiClass(context.type);

                    api.change(context, event);
                },
            ],
        },
        RESPONSE_CHANGE_EXTRA: {
            actions: enqueueActions(({ enqueue, context }) => {
                if (context.invalid) {
                    enqueue.raise({ type: 'VALIDATE' });
                }
            }),
        },
        TIMER_EXPIRED: {
            actions: [
                ({ context }) =>
                    sendParent({
                        type: 'TIMER_QUESTION_EXPIRED',
                        questionId: context.id,
                    }),
            ],
        },
        STOP_TIMER: {
            actions: ({ context }) => {
                context.timer?.send({
                    type: 'STOP',
                });
            },
        },
        LOAD_RESIDENCE: {
            // actions: assign((ctx, evt) => {})
        },
        // FILE_UPLOAD: {
        //     actions: assign(({ context, event }) => {

        //     })
        // },
        FILE_DELETE: {
            // actions: assign((ctx, evt) => {})
        },
        VALIDATE_FILES: {
            actions: enqueueActions(({ enqueue, context, event: { files } }) => {
                const { fileAmount, fileLimitExt } = context.params;
                const {
                    info: {
                        params: {
                            alert: { fileExt, fileMaxAmount, fileMaxSize },
                        },
                    },
                } = (context.parentRef.getSnapshot() as any).context;

                const invalidNameFiles = [...files].filter(({ name }) => !hasExtension(name, fileLimitExt));
                const invalidSizeFiles = [...files].filter(({ size }) => size > MAX_USER_FILE_SIZE);
                const invalidCountMessage =
                    files.length > fileAmount ? replaceMaxCount(fileMaxAmount, fileAmount) : null;

                const invalidNameMessage =
                    invalidSizeFiles.length > 0
                        ? replaceMaxSize(fileMaxSize, humanMaxUserFileSize) +
                          '\n' +
                          invalidSizeFiles.map(({ name, size }) => `${name} - <b>${fileSize(size)}</b>`).join('\n')
                        : '';

                const invalidTypeMessage =
                    invalidNameFiles.length > 0
                        ? replaceFileExt(fileExt, fileLimitExt.map((e) => `<b>${e}</b>`).join(', ')) +
                          '\n' +
                          invalidNameFiles
                              .map(({ name }) => `${name} - <b>${name.split('.').slice(-1)[0].toUpperCase()}</b>`)
                              .join('\n')
                        : '';

                const errorText = [invalidCountMessage, invalidNameMessage, invalidTypeMessage]
                    .filter(Boolean)
                    .join('\n\n');
                const valid = invalidNameFiles.length === 0 && invalidSizeFiles.length === 0 && !invalidCountMessage;
                enqueue.assign({
                    errorText,
                    valid,
                });
                if (valid) {
                    enqueue.raise({ type: 'FILE_UPLOAD', files });
                }
            }),
        },
        VALIDATE: {
            actions: [
                enqueueActions(({ enqueue, context, self }) => {
                    const { params, responseActor, type } = context;
                    // ctx.invalid = false;
                    // ctx.commentInvalid = false;
                    let invalid = false;
                    let commentInvalid = false;
                    const api = getApiClass(type);

                    if (
                        ![QuestionType.CSI, QuestionType.KANO].includes(type) &&
                        params.commentRequired &&
                        !responseActor.getSnapshot().context.comment
                    ) {
                        commentInvalid = true;
                    }

                    if (!isNotRequiredAnswers && !api.validate(context)) {
                        invalid = true;
                    }

                    const valid = !invalid && !commentInvalid;

                    enqueue.assign({
                        valid,
                        invalid,
                        commentInvalid,
                    });

                    if (valid && context.params.testAnswerReaction && !context.testAnswerReactingState) {
                        enqueue.raise({
                            type: 'CHECK_TEST_ANSWER',
                            data: mapQuestionForQueue(self as QuestionActorRef),
                        });
                    }
                }),
            ],
        },
        TOUCH: {
            actions: assign({ dirty: true }),
        },
        SET_DETAILS_LOADED: {
            actions: enqueueActions(({ enqueue }) => {
                enqueue.sendTo(({ context }) => context.parentRef, { type: 'SET_DETAILS_LOADED' });
            }),
        },
        SET_COMMENT_INVALID: {
            actions: assign(({ event: { value } }) => ({ commentInvalid: value })),
        },
        SET_INVALID: {
            actions: enqueueActions(({ enqueue, context, event }) => {
                enqueue.assign({ invalid: event.value });
                if (!event.value) {
                    context.answersActors.forEach((a) => a.send({ type: 'SET_VALID', value: true }));
                }
            }),
        },
        HIDE_TEST_ANSWER_REACTING_NOTIFICATION: {
            actions: enqueueActions(({ enqueue }) => {
                enqueue.assign({ testAnswerReactingState: TestAnswerReactingState.SHOWN });

                enqueue.sendTo(({ context }) => context.parentRef, {
                    type: 'SELECT_NEXT_BUNDLE',
                    noValidate: false,
                });
            }),
        },
    },
});

export type QuestionMachine = typeof QuestionMachineDefinition;

export type QuestionActorRef = ActorRefFrom<typeof QuestionMachineDefinition>;
export type QuestionActor = Actor<typeof QuestionMachineDefinition>;
